import React, {
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
  useContext,
} from 'react';
import './JobsChart.scss';
import { init, use, ECharts } from 'echarts/core';
import { BarChart, PieChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  millisecondsInHour,
  millisecondsInMinute,
  millisecondsInSecond,
} from 'date-fns';
import SpinnerChidori from 'components/SpinnerChidori/SpinnerChidori';
import { ECElementEvent } from 'echarts/types/dist/shared';
import { Card } from 'antd';
import DownloadLogsAction from 'components/DownloadLogsAction/DownloadLogsAction';
import SparkLogButton from 'components/SparkLogButton/SparkLogButton';
import SignedURLButton from 'components/SignedURLButton/SignedURLButton';
import useChidoriRoutes from 'hooks/useChidoriRoutes';
import ExternalLinkIcon from 'assets/icons/external-link.svg';
import { prepareDuration, convertTimestamp } from 'utils/date';
import { AppContext } from 'contexts/AppContext';
import { JobStatus } from 'components/StatusTag/StatusTag';
import { useGetChartJobsData } from 'hooks/jobs';
import useSignedURL from 'hooks/useSignedURL';
import { useGetClusterInfo } from 'hooks/cluster';

/**
 * @property {string} 0 - sparkJobId
 * @property {number} 1 - duration/millisecondsInMinute
 * @property {number} 2 - queueingTime
 * @property {number} 3 - duration
 * @property {Date} 4 - createdAt.
 */
type formattedDataType = [string, number, number, number, string];

const jobsHistoryBarChartColor = '#8BA3F4';
const jobsHistoryBarChartColorHovered = '#B0C3F8';

function JobsChart({
  tableName,
  schemaName,
  cachedFormatDuration,
}: {
  tableName: string;
  schemaName: string;
  cachedFormatDuration: ({
    duration,
    shorthand,
  }: {
    duration: Duration;
    shorthand?: boolean | undefined;
  }) => string;
}) {
  const intl = useIntl();
  const [selectedBarJobID, setSelectedbarJobID] = useState<string | null>(null);
  const { currentAppliedFilters, isFiltersApplied } = useContext(AppContext);
  const { clusterNameFromURL } = useChidoriRoutes();
  const clusterName = clusterNameFromURL;
  const { isSignedURL, expires, signature, prefix } = useSignedURL();
  const { data: clusterInfo } = useGetClusterInfo({ clusterName });
  const clusterURLFromClusterInfo = clusterInfo?.Url || '';

  const { data: jobsHistory, isLoading: isJobsHistoryLoading } =
    useGetChartJobsData({
      tableName,
      schemaName,
      clusterName,
      isSignedURL,
      ...(isSignedURL && expires ? { expires } : {}),
      ...(isSignedURL && signature ? { signature } : {}),
      ...(isSignedURL && prefix ? { prefix } : {}),
      ...(Object.keys(currentAppliedFilters)?.length
        ? { filters: currentAppliedFilters }
        : {}),
    });

  const jobInforCardRef = useRef<HTMLDivElement>(null);

  const chartRef = useRef<HTMLDivElement>(null);
  const eChartInstance = useRef<ECharts | null>(null);
  use([
    BarChart,
    PieChart,
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LabelLayout,
    UniversalTransition,
    CanvasRenderer,
  ]);

  const availableCharts = ['bar', 'pie'];
  const [
    selectedChart,
    // setSelectedChart
  ] = useState(availableCharts[0]);

  const cachedFormattedYAxis = useMemo(
    () => jobsHistory?.map((relatedJob) => relatedJob.duration / (1000 * 60)),
    [jobsHistory]
  );

  const cachedFormattedData: formattedDataType[] | undefined = useMemo(
    () =>
      jobsHistory?.map((relatedJob) => {
        return [
          relatedJob.sparkJobId,
          relatedJob.duration / millisecondsInMinute,
          relatedJob.queueingTime,
          relatedJob.duration,
          relatedJob.createdAt,
        ];
      }),
    [jobsHistory]
  );

  const onBarClick = (params: ECElementEvent) => {
    const jobID = (params.data as formattedDataType)[0] as string;
    setSelectedbarJobID((prev) => {
      const shouldSelect = jobID !== prev;

      eChartInstance.current?.dispatchAction({
        type: shouldSelect ? 'select' : 'unselect',
        seriesIndex: params.seriesIndex,
        dataIndex: params.dataIndex,
      });
      return shouldSelect ? jobID : null;
    });
  };

  const tooltipFormatter = useCallback((params: any) => {
    let tooltipContent = '';

    for (const key in params) {
      const param = params[key];

      const [
        jobId,
        ,
        queueingTimeInMSec,
        durationInMSec,
        createdAt,
      ]: formattedDataType = param.value;

      const duration = {
        hours: Math.floor((durationInMSec || 0) / millisecondsInHour),
        minutes: Math.floor(
          ((durationInMSec || 0) % millisecondsInHour) / millisecondsInMinute
        ),
        seconds: Math.floor(
          ((durationInMSec || 0) % millisecondsInMinute) / millisecondsInSecond
        ),
      };

      const queueingTime = {
        hours: Math.round((queueingTimeInMSec || 0) / millisecondsInHour),
        minutes: Math.round(
          ((queueingTimeInMSec || 0) % millisecondsInHour) /
            millisecondsInMinute
        ),
        seconds: Math.ceil(
          ((queueingTimeInMSec || 0) % millisecondsInMinute) /
            millisecondsInSecond
        ),
      };

      let startDate = '';
      if (createdAt) {
        try {
          const date = new Date(createdAt); // safe parse from ISO string
          if (!isNaN(date.getTime())) {
            const { dateFormatted, timeFormatted } = convertTimestamp(
              date.toString()
            );
            startDate = `${dateFormatted}, ${timeFormatted}`;
          }
        } catch {
          startDate = '';
        }
      }

      tooltipContent += `<section class="job-history__chart-content">      
        <article class="job-history__chart-line table-name">
          <div class="job-history__chart-item-icon"></div>
          <span class="job-history__chart-property-value table-name">
           ${tableName}
         </span>
          </article>

        <article class="job-history__chart-line">
          <span class="job-history__chart-property-name">
            ${intl.formatMessage({ id: 'jobsHistoryChart.tooltip.id' })}:
          </span>
          <span class="job-history__chart-property-value">
            ${jobId}          
          </span>
        </article>

        <article class="job-history__chart-line">
          <span class="job-history__chart-property-name">
            ${intl.formatMessage({ id: 'jobsHistoryChart.tooltip.start' })}:
          </span>
          <span class="job-history__chart-property-value">
            ${startDate}
          </span>
        </article>

        <article class="job-history__chart-line">
          <span class="job-history__chart-property-name">
            ${intl.formatMessage({ id: 'jobsHistoryChart.tooltip.duration' })}:
          </span>
          <span class="job-history__chart-property-value">
            ${cachedFormatDuration({
              duration,
              shorthand: true,
            })}
          </span>
        </article>
        
        <article class="job-history__chart-line">
          <span class="job-history__chart-property-name">
            ${intl.formatMessage({
              id: 'jobsHistoryChart.tooltip.queueingTime',
            })}:
          </span>
          <span class="job-history__chart-property-value">
            ${cachedFormatDuration({
              duration: queueingTime,
              shorthand: true,
            })}
          </span>
        </article>
      </section>`;
    }

    return tooltipContent;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pieChartTooltipFormatter = useCallback((params: any) => {
    const valueInMSec = params.value;
    const duration = {
      hours: Math.floor((valueInMSec || 0) / millisecondsInHour),
      minutes: Math.floor(
        ((valueInMSec || 0) % millisecondsInHour) / millisecondsInMinute
      ),
      seconds: Math.floor(
        ((valueInMSec || 0) % millisecondsInMinute) / millisecondsInSecond
      ),
    };
    return `${params.name}\n ${cachedFormatDuration({
      duration,
      shorthand: true,
    })} \n (${params.percent}%)`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seriesCommonConfigs = {
    name: 'duration',
    type: selectedChart,
  };

  const [isFiltersAppliedOnChart, setIsFiltersAppliedOnChart] = useState(false);

  useEffect(() => {
    const handleResize = () => eChartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (jobsHistory) {
      const chartOptions = eChartInstance.current?.getOption();
      if (
        isFiltersApplied ||
        (!isFiltersApplied && isFiltersAppliedOnChart) ||
        !chartOptions ||
        (chartOptions &&
          (chartOptions.series as any[])[0].type !== selectedChart)
      ) {
        eChartInstance.current?.dispose?.();
        if (chartRef.current) {
          eChartInstance.current = init(chartRef.current);
        }
        if (eChartInstance.current) {
          eChartInstance.current.setOption({
            tooltip: {
              trigger: 'axis',
              position: function (pt: any) {
                return [pt[0], '10%'];
              },
              formatter: (params: any) => {
                return tooltipFormatter(params);
              },
            },
            ...(selectedChart === 'bar' && {
              grid: {
                backgroundColor: 'white',
                show: true,
                top: 30,
                bottom: 150,
              },
              xAxis: {
                name: 'Jobs',
                type: 'category',
                axisLabel: {
                  margin: 30,
                  interval: 0,
                  rotate: 35,
                  formatter: (value: string) => {
                    return (
                      '...' + value.slice(value.lastIndexOf('e-'), value.length)
                    );
                  },
                },
                axisPointer: {
                  type: 'shadow',
                },
              },
              yAxis: {
                name: 'duration (min)',
                type: 'value',
                axisLabel: {
                  height: '450px',
                },
                axisLine: {
                  show: true,
                },
                data: cachedFormattedYAxis,
                minInterval: 1,
              },
              series: [
                {
                  ...seriesCommonConfigs,
                  data: cachedFormattedData,
                  barMaxWidth: 50,
                  color: jobsHistoryBarChartColor,
                  emphasis: {
                    itemStyle: {
                      color: jobsHistoryBarChartColorHovered, // Color for hovered bar
                    },
                  },
                  selectedMode: 'single', // Allow only one bar to be selected at a time
                },
              ],
            }),
            ...(selectedChart === 'pie' && {
              legend: {
                top: 45,
                left: 'center',
              },
              series: [
                {
                  ...seriesCommonConfigs,
                  radius: ['50%', '70%'],
                  avoidLabelOverlap: false,
                  itemStyle: {
                    borderRadius: 10,
                    borderColor: '#fff',
                    borderWidth: 2,
                  },
                  label: {
                    show: false,
                    position: 'center',
                    formatter: function (params: any) {
                      return pieChartTooltipFormatter(params);
                    },
                  },
                  emphasis: {
                    label: {
                      show: true,
                      fontSize: 14,
                    },
                  },
                  data: jobsHistory?.map((relatedJob) => {
                    return {
                      value: relatedJob.duration,
                      name: relatedJob.sparkJobId,
                    };
                  }),
                },
              ],
            }),
          });
          eChartInstance.current?.resize();
          eChartInstance.current.on('click', 'series.bar', onBarClick);
        }
        setIsFiltersAppliedOnChart(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsHistory, selectedChart, currentAppliedFilters]);

  function JobPropertyElement({
    label,
    value,
  }: {
    label: string;
    value: string;
  }) {
    return (
      <section className="job-history__job-info-card-element">
        <article className="job-history__job-info-card-property-name">
          {label}
        </article>
        <article className="job-history__job-info-card-property-value">
          {value}
        </article>
      </section>
    );
  }

  const selectedJob = jobsHistory?.find(
    (job) => job.sparkJobId === selectedBarJobID
  );
  const clusterPrefix = selectedJob?.clusterPrefix;
  const jobStatus = selectedJob?.sparkJobStatus;
  const isDownloadLogsBtnDisabled = jobStatus === JobStatus.In_Queue;
  const sparkAppID = selectedJob?.sparkAppId;
  const createdAt = selectedJob?.createdAt;
  let startDate;
  if (createdAt) {
    const { dateFormatted, timeFormatted } = convertTimestamp(
      createdAt ? createdAt?.toString() : ''
    );
    startDate = `${dateFormatted}, ${timeFormatted}`;
  }
  const durationInMSec = selectedJob?.duration ?? 0;
  const duration = prepareDuration(durationInMSec);

  const currentqueueingTime = selectedJob?.queueingTime ?? 0;
  const queueingTimeInMSec = currentqueueingTime > 0 ? currentqueueingTime : 0;

  const queueingTime = prepareDuration(queueingTimeInMSec);
  const vendor = selectedJob?.vendor;
  const language = selectedJob?.mvLanguage;

  const showSelectedJobCard =
    selectedBarJobID &&
    clusterPrefix &&
    clusterURLFromClusterInfo &&
    jobStatus &&
    startDate &&
    duration &&
    vendor;

  useEffect(() => {
    if (showSelectedJobCard) {
      jobInforCardRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showSelectedJobCard]);

  return (
    <div className="job-history__chart-wrapper">
      <h3>
        <FormattedMessage id="jobInfo.jobsDuration" />
      </h3>
      {isJobsHistoryLoading ? (
        <SpinnerChidori />
      ) : (
        <>
          <div className="job-history__chart" ref={chartRef} />
          {showSelectedJobCard && (
            <Card
              size="small"
              title={
                <article className="job-history__job-info-card-title">
                  {`${intl.formatMessage({
                    id: 'jobsHistoryChart.card.id',
                  })}: ${selectedBarJobID}`}
                </article>
              }
              className="job-history__job-info-card"
              ref={jobInforCardRef}>
              <section className="job-history__job-info-card-body">
                <JobPropertyElement
                  label={intl.formatMessage({
                    id: 'jobsHistoryChart.card.start',
                  })}
                  value={startDate || ''}
                />
                <JobPropertyElement
                  label={intl.formatMessage({
                    id: 'jobsHistoryChart.card.duration',
                  })}
                  value={cachedFormatDuration({
                    duration: duration,
                    shorthand: true,
                  })}
                />
                <JobPropertyElement
                  label={intl.formatMessage({
                    id: 'jobsHistoryChart.card.queueingTime',
                  })}
                  value={cachedFormatDuration({
                    duration: queueingTime,
                    shorthand: true,
                  })}
                />
                <JobPropertyElement
                  label={intl.formatMessage({
                    id: 'jobsHistoryChart.card.mV',
                  })}
                  value={tableName}
                />
                <JobPropertyElement
                  label={intl.formatMessage({
                    id: 'jobsHistoryChart.card.service',
                  })}
                  value={vendor}
                />
                <JobPropertyElement
                  label={intl.formatMessage({
                    id: 'jobsHistoryChart.card.language',
                  })}
                  value={language || ''}
                />
                {/* // */}
              </section>
              <section className="job-history__job-info-card-actions">
                <section className="job-history__job-info-card-action-item download-logs">
                  <DownloadLogsAction
                    jobId={selectedBarJobID}
                    isDisabled={isDownloadLogsBtnDisabled}
                  />
                </section>
                <section className="job-history__job-info-card-action-item">
                  <SignedURLButton
                    clusterName={clusterName}
                    tableName={tableName}
                    jobId={selectedBarJobID}
                    prefix={clusterPrefix}
                    schema={schemaName}
                  />
                </section>
                <section className="job-history__job-info-card-action-item">
                  <SparkLogButton
                    clusterUrl={clusterURLFromClusterInfo}
                    jobStatus={jobStatus}
                    sparkAppID={sparkAppID}
                  />
                  <img src={ExternalLinkIcon} alt="External Link" />
                </section>
              </section>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default JobsChart;
